import { useEffect, useMemo, useState } from "react";
import { TableSortState } from "components/table";
import { useAppDispatch, useAppSelector } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { addFleetPageRouteName } from "../../add/add.page";
import { draftFleetPageRouteName } from "../../draft/index.page";
import { fleetApi } from "_services/modules/transport-resource/fleet";
import { env } from "config/env";
import { useVehicleTypeServiceV1GetVehicleTypeDropdownQuery } from "_services/modules/vehicle-type";
import { useVehicleModelServiceV1GetVehicleModelDropdownQuery } from "_services/modules/vehicle-model";
import { useFleetColumns } from "./useFleetColumns";
import { detailFleetPageRouteName } from "../../index.page";

import { 
  setBulkAction,
  showResultModal,
  hideResultModal,
} from "../store/fleetSlice";

import { useFleetQuery } from "./useFleetQuery";
import { useFleetActions } from "./useFleetActions";
import { useFleetSelection } from "./useFleetSelection";
import { FleetRow } from "../types";

const useFleetList = () => {
  const { t } = useAppTranslation("fleet");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Local UI State (Toggles)
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [exportType, setExportType] = useState("all");
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<FleetRow | null>(null);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [stagedTarget, setStagedTarget] = useState<{ ids: string[], plateNumbers: string[] } | null>(null);

  // Local UI State (Filters & Pagination) — isolated from the main list's Redux state
  const [searchValue, setSearchValue] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("");
  const [vehicleModelFilter, setVehicleModelFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Redux Session State
  const bulkActionStatus = useAppSelector(state => state.fleet.bulkActionStatus);
  const showBulkConfirmModal = useAppSelector(state => state.fleet.showBulkConfirmModal);
  const resultModal = useAppSelector(state => state.fleet.resultModal);

  // Specialized Hooks
  const { tableData, isLoading, isFetching, totalPages } = useFleetQuery(
    sortState, currentPage, rowsPerPage, searchValue, vehicleTypeFilter, vehicleModelFilter,
  );
  const {
    selectedKeys,
    setSelectedKeys,
    selectedPlateNumbers,
    isBulkDisabled,
    allowedBulkActions,
  } = useFleetSelection(tableData, [
    currentPage,
    searchValue,
    vehicleTypeFilter,
    vehicleModelFilter,
    sortState,
  ], true);

  const { 
    handleStatusUpdate, 
    handleConfirmDelete, 
    handleExport, 
    isImportLoading, 
    isExportLoading, 
    isBulkUpdating,
    importFleets,
    handleEditRequest,
  } = useFleetActions();
  
  const isFilterActive = !!searchValue || !!vehicleTypeFilter || !!vehicleModelFilter;
  
  const onClearFilter = () => {
    setSelectedKeys([]);
    setSearchValue("");
    setVehicleTypeFilter("");
    setVehicleModelFilter("");
    setCurrentPage(1);
  };

  useEffect(() => {
    dispatch(setGlobalComponent({ title: t("page-title-list"), hasBackButton: false }));
  }, [dispatch, t]);

  const { data: vehicleTypeData, isLoading: isVehicleTypeLoading } = useVehicleTypeServiceV1GetVehicleTypeDropdownQuery();
  const vehicleTypeOptions = useMemo(() => {
    const options = (vehicleTypeData?.vehicle_types || []).map((item: any) => ({ label: item.name, value: item.id }));
    return [{ label: t("filter-type-placeholder"), value: "" }, ...options];
  }, [vehicleTypeData, t]);
  
  const { data: vehicleModelData } = useVehicleModelServiceV1GetVehicleModelDropdownQuery({
    vehicleTypeId: vehicleTypeFilter
  });
  const vehicleModelOptions = useMemo(() => {
    const options = (vehicleModelData?.vehicle_models || []).map((item: any) => ({ label: item.name, value: item.id }));
    return [{ label: t("filter-model-placeholder"), value: "" }, ...options];
  }, [vehicleModelData, t]);

  const columns = useFleetColumns({
    t,
    handleEditRequest,
    handleDeleteRequest: (row: FleetRow) => {
      setStagedTarget({ ids: [String(row.id)], plateNumbers: [row.plateNumber] });
      dispatch(setBulkAction({ status: "Delete", showModal: true }));
    },
    handleToggleStatus: async (row: FleetRow, status: string) => {
      setStagedTarget({ ids: [String(row.id)], plateNumbers: [row.plateNumber] });
      dispatch(setBulkAction({ status, showModal: true }));
    },
  });

  const handleSubmitImport = async () => {
    if (!selectedFile) return;
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const lang = localStorage.getItem("app-language") || "id";
      const result = await importFleets({ 
        apiHttpBody: formData, 
        lang: lang.startsWith("id") ? "id" : "en" 
      }).unwrap();
      setImportResult(result);
      setIsImportOpen(false);
      dispatch(showResultModal({
        type: "success",
        title: t("import-success-title" as any),
        message: t("import-success-message" as any, { count: result.imported_count || 0 }),
      }));
    } catch (error: any) {
      if (error.data?.error_file_url) {
        const a = document.createElement("a");
        a.href = error.data.error_file_url;
        a.download = "import_errors.xlsx";
        a.click();
        
        const failedCount = error.data?.failed_count || 0;
        const localizedMessage = failedCount > 0
          ? t(failedCount === 1 ? ("import-partial-status-singular" as any) : ("import-partial-status" as any), { count: failedCount })
          : (error.data?.message || t("import-failed-message" as any));

        dispatch(showResultModal({
          type: "error",
          title: t("import-failed-title" as any),
          message: localizedMessage,
        }));
        setIsImportOpen(false);
      } else {
        toast.error(t("toast-import-failed" as any));
      }
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const baseUrl = env.REST_HOST.replace(/\/$/, "");
      const response = await fetch(`${baseUrl}/transport-resource/v1/fleets/import/template`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fleet_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("toast-download-failed" as any));
    }
  };

  const onRowClick = (row: FleetRow) => navigate(`${addFleetPageRouteName}/${row.id}`, { state: { from: "draft" } });

  return {
    t,
    isLoading,
    isFetching,
    totalPages,
    totalSelected: selectedKeys.length,
    importResult,
    showImportStatusModal: resultModal.show,
    setShowImportStatusModal: (show: boolean) => !show && dispatch(hideResultModal()),
    importStatusTitle: resultModal.title,
    importStatusMessage: resultModal.message,
    importStatusType: resultModal.type,
    showBulkConfirmModal,
    setShowBulkConfirmModal: (show: boolean) => dispatch(setBulkAction({ status: bulkActionStatus, showModal: show })),
    bulkActionStatus: bulkActionStatus || "",
    onConfirmBulkStatusUpdate: () => {
      if (stagedTarget) {
        handleStatusUpdate(stagedTarget.ids, stagedTarget.plateNumbers, bulkActionStatus || "");
        setSelectedKeys([]);
      }
    },
    onConfirmDelete: () => handleConfirmDelete(deleteRow, setShowDeleteConfirmModal),
    onAddFleet: () => navigate(addFleetPageRouteName),
    onDraft: () => navigate(draftFleetPageRouteName),
    onExportClick: () => {
      setExportType(selectedKeys.length > 0 ? "selected" : "all");
      setIsExportOpen(true);
    },
    onExport: () => handleExport(exportFormat, exportType, setIsExportOpen, {
      search: searchValue,
      status: "Draft",
      vehicle_type_id: vehicleTypeFilter,
      vehicle_model_id: vehicleModelFilter,
    }, selectedKeys),
    onCloseExport: () => setIsExportOpen(false),
    onExportFormatChange: setExportFormat,
    onExportTypeChange: setExportType,
    exportFormat,
    exportType,
    isExportOpen,
    onImport: () => setIsImportOpen(true),
    onCloseImport: () => { setIsImportOpen(false); setSelectedFile(null); setImportResult(null); },
    onFileSelect: setSelectedFile,
    onFileRemove: () => setSelectedFile(null),
    onTemplateDownload: handleTemplateDownload,
    onSubmitImport: handleSubmitImport,
    selectedFile,
    isImportOpen,
    isImportLoading,
    isExportLoading,
    isBulkUpdating,
    isBulkDisabled,
    allowedBulkActions,
    onBulkStatusUpdate: (status: string) => {
      setStagedTarget({ ids: selectedKeys as string[], plateNumbers: selectedPlateNumbers });
      dispatch(setBulkAction({ status, showModal: true }));
    },
    isFilterActive,
    onClearFilter,
    modalTargetPlateNumbers: stagedTarget?.plateNumbers || [],
    modalTargetKeys: stagedTarget?.ids || [],
    paginatedRows: tableData,
    rowsPerPage,
    currentPage,
    setCurrentPage: (page: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setCurrentPage(page);
    },
    setRowsPerPage: (rows: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setRowsPerPage(rows);
    },
    searchValue,
    setSearchValue: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setSearchValue(val);
    },
    vehicleTypeFilter,
    setVehicleTypeFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setVehicleTypeFilter(val);
    },
    vehicleModelFilter,
    setVehicleModelFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setVehicleModelFilter(val);
    },
    vehicleTypeOptions,
    vehicleModelOptions,
    selectedKeys,
    selectedPlateNumbers,
    setSelectedKeys,
    sortState,
    setSortState: (sort: TableSortState | ((prev: TableSortState) => TableSortState)) => {
      setSelectedKeys([]);
      setSortState(sort);
    },
    showDeleteConfirmModal,
    setShowDeleteConfirmModal,
    deleteRow,
    columns,
    onRowClick,
  };
};

export default useFleetList;
